import type { Rule } from 'antd/es/form';

import type { ConnectorConfig, ConnectorField, ConnectorFieldValidationRules } from '@/entities/connector';

const parseNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const templatePatternToRegex = (pattern: string): RegExp | null => {
  const trimmed = pattern.trim();

  const isRegexFormat = trimmed.startsWith('^') || trimmed.startsWith('/');
  if (!isRegexFormat) return null;

  try {
    const regexStr = trimmed.startsWith('/') ? trimmed.slice(1).replace(/\/.*$/, '') : trimmed;
    return new RegExp(regexStr);
  } catch {
    return null;
  }
};

const buildNumberRangeMessage = (fieldKey: string, min: number | undefined, max: number | undefined): string => {
  if (min !== undefined && max !== undefined) {
    return `${fieldKey}은(는) ${min}~${max} 사이여야 합니다`;
  }
  if (min !== undefined) {
    return `${fieldKey}은(는) ${min} 이상이어야 합니다`;
  }
  return `${fieldKey}은(는) ${max} 이하이어야 합니다`;
};

const buildNumberRangeRules = (field: ConnectorField, validationRules: ConnectorFieldValidationRules): Rule[] => {
  const { min, max } = validationRules;
  return [
    {
      validator: async (_rule, value) => {
        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty) return;

        const numValue = parseNumericValue(value);
        if (numValue === null) {
          throw new Error(`${field.label}은(는) 숫자여야 합니다`);
        }

        if (min !== undefined && numValue < min) {
          throw new Error(buildNumberRangeMessage(field.label, min, max));
        }

        if (max !== undefined && numValue > max) {
          throw new Error(buildNumberRangeMessage(field.label, min, max));
        }
      },
    },
  ];
};

const buildPatternRules = (validationRules?: ConnectorFieldValidationRules): Rule[] => {
  if (!validationRules) return [];

  const { pattern } = validationRules;
  if (!pattern) return [];

  const regex = templatePatternToRegex(pattern);
  if (!regex) return [];

  return [
    {
      pattern: regex,
      message: `형식에 맞게 입력해주세요 (예: ${pattern})`,
    },
  ];
};

const buildValidationRules = (field: ConnectorField): Rule[] => {
  const validationRules = field.validationRules;
  const isNumberField = field.inputType === 'number';
  const numberRules = isNumberField ? buildNumberRangeRules(field, validationRules ?? {}) : [];
  const patternRules = buildPatternRules(validationRules);

  return [...numberRules, ...patternRules];
};

export const buildConnectorFieldRules = (field: ConnectorField): Rule[] | undefined => {
  const rules: Rule[] = [];

  if (field.required) {
    rules.push({ required: true, message: `${field.label}은(는) 필수입니다` });
  }
  rules.push(...buildValidationRules(field));

  return rules.length > 0 ? rules : undefined;
};

type FieldValidationResult = { ok: true } | { ok: false; errorMessage: string };
type ConnectorFieldPartitionResult = {
  validMappedConfig: ConnectorConfig;
  invalidFieldMessages: Record<string, string>;
  unmappedConfig: ConnectorConfig;
  missingMappedKeys: string[];
};

const validateFieldValue = (field: ConnectorField, value: unknown): string | null => {
  const isEmpty = value === undefined || value === null || value === '';

  if (field.required && isEmpty) {
    return `${field.label}은(는) 필수입니다`;
  }

  if (isEmpty) return null;

  const validationRules = field.validationRules;
  if (!validationRules) return null;

  if (field.inputType === 'number') {
    const numValue = parseNumericValue(value);
    if (numValue === null) {
      return `${field.label}은(는) 숫자여야 합니다`;
    }
    const { min, max } = validationRules;
    if (min !== undefined && numValue < min) {
      return buildNumberRangeMessage(field.label, min, max);
    }
    if (max !== undefined && numValue > max) {
      return buildNumberRangeMessage(field.label, min, max);
    }
  }

  if (validationRules.pattern && typeof value === 'string') {
    const regex = templatePatternToRegex(validationRules.pattern);
    if (regex && !regex.test(value)) {
      return `형식에 맞게 입력해주세요 (예: ${validationRules.pattern})`;
    }
  }

  return null;
};

export const partitionConnectorConfigByFields = (
  config: ConnectorConfig,
  fields: readonly ConnectorField[]
): ConnectorFieldPartitionResult => {
  const fieldMap = new Map(fields.map((field) => [field.label, field]));
  const validMappedConfig: ConnectorConfig = {};
  const invalidFieldMessages: Record<string, string> = {};
  const unmappedConfig: ConnectorConfig = {};

  for (const field of fields) {
    if (!(field.label in config)) {
      continue;
    }

    const value = config[field.label];
    const errorMessage = validateFieldValue(field, value);

    if (errorMessage) {
      invalidFieldMessages[field.label] = errorMessage;
      continue;
    }

    validMappedConfig[field.label] = value;
  }

  for (const [key, value] of Object.entries(config)) {
    if (fieldMap.has(key)) {
      continue;
    }

    unmappedConfig[key] = value;
  }

  const missingMappedKeys = fields.map((field) => field.label).filter((fieldLabel) => !(fieldLabel in config));

  return {
    validMappedConfig,
    invalidFieldMessages,
    unmappedConfig,
    missingMappedKeys,
  };
};

export const validateConfigWithFieldRules = (
  config: ConnectorConfig,
  fields: readonly ConnectorField[]
): FieldValidationResult => {
  for (const field of fields) {
    const errorMessage = validateFieldValue(field, config[field.label]);
    if (errorMessage) {
      return { ok: false, errorMessage };
    }
  }
  return { ok: true };
};

export const normalizeConnectorConfigByFields = (
  fields: readonly ConnectorField[],
  config?: ConnectorConfig | null
): ConnectorConfig => {
  if (!config) {
    return {};
  }

  const fieldMap = new Map(fields.map((field) => [field.label, field]));

  const normalizedEntries = Object.entries(config).map(([key, value]) => {
    const field = fieldMap.get(key);
    const isNumberField = field?.inputType === 'number';

    if (!isNumberField) {
      return [key, value ?? ''];
    }

    const normalizedValue = parseNumericValue(value);

    if (normalizedValue === null || normalizedValue === undefined) {
      return [key, value ?? ''];
    }

    return [key, normalizedValue];
  });

  return Object.fromEntries(normalizedEntries);
};

export const getConnectorFieldPlaceholder = (field: ConnectorField): string => {
  return `${field.label}을(를) 입력해주세요`;
};
